import {
  ABILITY_COUNT,
  Abilities,
  AbilityId,
  Actor,
  Bracing,
  Casting,
  Dashing,
  Falling,
  Hitstun,
  InputLocked,
  Instability,
  Kit,
  PlayerTag,
  Stunned,
  Transform,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import {
  ABILITIES,
  BRACE,
  INSTABILITY_DECAY,
  INSTABILITY_GRACE,
  abilityDef,
} from '@/game/abilities/definitions'
import { ABILITY_FIRE, resolveDashContacts } from '@/game/abilities/handlers'
import { signatureFor } from '@/game/abilities/signatures'
import { Events } from '@/game/events'
import { EventsResource } from '@/game/resources'
import { InputResource } from '@/input/InputManager'

/**
 * Owns everything time-based about a character's kit: charge recharge, the
 * windup → active → recover cast pipeline, the dash and brace states, stun,
 * and instability decay.
 *
 * Effects themselves live in `abilities/handlers.ts`; this system only decides
 * *when* they fire. Keeping the two apart is what makes adding an ability a
 * matter of one table row and one function.
 */
export function abilitySystem({ world, dt }: SystemContext): void {
  const input = world.tryResource(InputResource)

  for (const e of world.query([PlayerTag, Actor, Abilities, Transform]).entities()) {
    const abilities = world.read(e, Abilities)
    const tag = world.read(e, PlayerTag)

    tickCharges(abilities, dt)
    tickBuffers(abilities, dt)
    tickInstability(world, e, dt)
    tickStun(world, e, dt)
    tickDash(world, e, dt)
    tickBrace(world, e, dt, input?.players[tag.player]?.brace.down ?? false)
    tickCast(world, e, dt)

    if (canStart(world, e)) tryStart(world, e, dt)
  }
}

function tickCharges(abilities: ReturnType<typeof Abilities.create>, dt: number): void {
  for (let i = 0; i < ABILITY_COUNT; i++) {
    if (abilities.charges[i] >= abilities.maxCharges[i]) {
      abilities.recharge[i] = 0
      continue
    }
    abilities.recharge[i] -= dt
    if (abilities.recharge[i] <= 0) {
      abilities.charges[i]++
      abilities.recharge[i] =
        abilities.charges[i] < abilities.maxCharges[i] ? abilities.cooldown[i] : 0
    }
  }
}

function tickBuffers(abilities: ReturnType<typeof Abilities.create>, dt: number): void {
  for (let i = 0; i < ABILITY_COUNT; i++) {
    if (abilities.buffer[i] > 0) abilities.buffer[i] = Math.max(0, abilities.buffer[i] - dt)
  }
}

function tickInstability(world: World, e: Entity, dt: number): void {
  const inst = world.get(e, Instability)
  if (!inst) return
  inst.sinceHit += dt
  if (inst.sinceHit < INSTABILITY_GRACE || inst.value <= 0) return
  const rate = INSTABILITY_DECAY * (world.has(e, Bracing) ? BRACE.decayMul : 1)
  inst.value = Math.max(0, inst.value - rate * dt)
}

function tickStun(world: World, e: Entity, dt: number): void {
  const stun = world.get(e, Stunned)
  if (stun) {
    stun.timeLeft -= dt
    if (stun.timeLeft <= 0) world.remove(e, Stunned)
  }

  const hitstun = world.get(e, Hitstun)
  if (hitstun) {
    hitstun.timeLeft -= dt
    if (hitstun.timeLeft <= 0) world.remove(e, Hitstun)
  }
}

function tickDash(world: World, e: Entity, dt: number): void {
  const dash = world.get(e, Dashing)
  if (!dash) return
  resolveDashContacts(world, e)
  dash.timeLeft -= dt
  if (dash.timeLeft <= 0) world.remove(e, Dashing)
}

function tickBrace(world: World, e: Entity, dt: number, held: boolean): void {
  const brace = world.get(e, Bracing)
  if (!brace) return

  brace.timeLeft -= dt
  brace.elapsed += dt
  const expired = brace.timeLeft <= 0
  // A released stance still owes its minimum: see BRACE.minHold.
  if ((held || brace.elapsed < BRACE.minHold) && !expired) return

  world.remove(e, Bracing)
  endBrace(world, e)
}

/** Brace's cooldown starts on release, so holding it does not stall the timer. */
function endBrace(world: World, e: Entity): void {
  // The cast that opened the stance is still in its `active` window; releasing
  // early must close it too, or the character stays locked out of everything.
  const cast = world.get(e, Casting)
  if (cast && cast.ability === AbilityId.Brace) world.remove(e, Casting)

  const abilities = world.read(e, Abilities)
  abilities.charges[AbilityId.Brace] = 0
  abilities.recharge[AbilityId.Brace] = abilities.cooldown[AbilityId.Brace]

  const tr = world.read(e, Transform)
  const tag = world.read(e, PlayerTag)
  world.resource(EventsResource).emit(Events.Ability, {
    entity: e,
    player: tag.player,
    ability: AbilityId.Brace,
    phase: 'end',
    x: tr.x,
    z: tr.z,
    angle: tr.angle,
  })
}

function tickCast(world: World, e: Entity, dt: number): void {
  const cast = world.get(e, Casting)
  if (!cast) return

  const def = abilityDef(cast.ability)
  cast.timer -= dt
  if (cast.timer > 0) return

  switch (cast.phase) {
    case 'windup':
      cast.phase = 'active'
      cast.timer = def.active
      fire(world, e, cast.ability)
      break
    case 'active':
      cast.phase = 'recover'
      cast.timer = def.recover
      break
    case 'recover':
      world.remove(e, Casting)
      break
  }
}

function fire(world: World, e: Entity, ability: number): void {
  const tr = world.read(e, Transform)
  const tag = world.read(e, PlayerTag)

  // A fighter's own move for this slot wins over the shared one. This is the
  // whole of what makes six fighters six fighters; see abilities/signatures.ts.
  const signature = signatureFor(world, e, ability)
  if (signature) {
    signature(world, e)
  } else if (ability === AbilityId.Brace) {
    world.add(e, Bracing, { timeLeft: BRACE.maxHold * (world.get(e, Kit)?.braceHold ?? 1) })
  } else {
    ABILITY_FIRE[ability]?.(world, e)
  }

  world.resource(EventsResource).emit(Events.Ability, {
    entity: e,
    player: tag.player,
    ability,
    phase: 'fire',
    x: tr.x,
    z: tr.z,
    angle: tr.angle,
  })
}

function canStart(world: World, e: Entity): boolean {
  return (
    !world.has(e, Casting) &&
    !world.has(e, Stunned) &&
    // Hitstun locks out abilities too — being launched has to cost you the
    // exchange, not merely your position.
    !world.has(e, Hitstun) &&
    !world.has(e, Falling) &&
    !world.has(e, Dashing) &&
    !world.has(e, Bracing) &&
    !world.has(e, InputLocked)
  )
}

function tryStart(world: World, e: Entity, _dt: number): void {
  const abilities = world.read(e, Abilities)
  const tag = world.read(e, PlayerTag)
  const input = world.tryResource(InputResource)

  for (const def of ABILITY_PRIORITY) {
    const id = def.id
    const wanted = def.hold
      ? (input?.players[tag.player]?.brace.down ?? false)
      : abilities.buffer[id] > 0
    if (!wanted) continue
    if (abilities.charges[id] <= 0) continue

    abilities.charges[id]--
    abilities.buffer[id] = 0
    if (abilities.charges[id] < abilities.maxCharges[id] && abilities.recharge[id] <= 0) {
      abilities.recharge[id] = abilities.cooldown[id]
    }

    const tr = world.read(e, Transform)
    world.add(e, Casting, { ability: id, phase: 'windup', timer: def.windup })
    world.resource(EventsResource).emit(Events.Ability, {
      entity: e,
      player: tag.player,
      ability: id,
      phase: 'cast',
      x: tr.x,
      z: tr.z,
      angle: tr.angle,
    })

    // A zero-windup ability (Dash) must land on the same step it was requested,
    // otherwise the input feels like it dropped a frame.
    if (def.windup <= 0) tickCast(world, e, 0)
    return
  }
}

/**
 * When two buttons are pressed on the same step, the more urgent one wins.
 * Dash first: it is the escape tool, and losing it to a queued push would feel
 * like the game ignored a life-saving input.
 */
const ABILITY_PRIORITY = [
  ABILITIES[AbilityId.Dash],
  ABILITIES[AbilityId.Brace],
  ABILITIES[AbilityId.Push],
  ABILITIES[AbilityId.Wave],
  ABILITIES[AbilityId.Vortex],
]
